import path from "node:path";

import type { PackageMetadata } from "./package-metadata.js";
import { packageMetadata } from "./package-metadata.js";

export type CliParseResult =
  | {
      kind: "start-server";
    }
  | {
      kind: "print-help";
    }
  | {
      kind: "print-version";
    }
  | {
      kind: "run-health-check";
      projectRoot?: string;
    }
  | {
      kind: "error";
      reason: "unknown-argument" | "invalid-arguments" | "invalid-health-path";
      args: string[];
    };

type WritableLike = {
  write: (chunk: string) => boolean | void;
};

export type RunCliDependencies = {
  stdout: WritableLike;
  stderr: WritableLike;
  startServer: () => Promise<void>;
  runHealthCheck: (input: { projectRoot?: string }) => Promise<number>;
  metadata?: PackageMetadata;
};

function buildCliCommand(metadata: PackageMetadata): string {
  return `npx ${metadata.name}@latest`;
}

export function formatUsageText(
  metadata: PackageMetadata = packageMetadata,
): string {
  const command = buildCliCommand(metadata);
  return [
    "Usage:",
    `  ${command}`,
    `  ${command} --help`,
    `  ${command} --version`,
    `  ${command} --health`,
    `  ${command} --health /absolute/project/path`,
  ].join("\n");
}

export function formatHelpText(
  metadata: PackageMetadata = packageMetadata,
): string {
  return [
    "PrimeUI MCP",
    "MCP stdio server for importing PrimeUI pages into your local project.",
    "",
    formatUsageText(metadata),
    "",
    "Options:",
    "  -h, --help       Show this help message and exit.",
    "  -v, --version    Show package version and exit.",
    "  --health         Run runtime diagnostics without starting the MCP server.",
    "",
    "Behavior:",
    "  Running without arguments starts the PrimeUI MCP stdio server.",
    "  This mode is intended to be launched by an MCP-compatible client.",
    "  `--health` prints runtime diagnostics and exits.",
    "",
    "Configuration:",
    "  PRIMEUI_API_KEY       PrimeUI API key. Falls back to .primeui/project.json.",
    "  PRIMEUI_API_BASE_URL  Override PrimeUI API base URL for local development.",
    "  PRIMEUI_PROJECT_ROOT  Override project root used to resolve .primeui/project.json.",
    "",
    "Project config:",
    "  .primeui/project.json must include projectId and apiKey.",
    "  targetProjectPath is required for file-oriented import/export flows.",
    "  targetProjectPath is optional for the additional atomic external API tools.",
  ].join("\n");
}

export function formatInvalidHealthPathError(
  projectRoot: string,
  metadata: PackageMetadata = packageMetadata,
): string {
  return [
    `Invalid --health project path: ${projectRoot}`,
    "",
    "The optional `--health` project path must be an absolute path.",
    "",
    formatUsageText(metadata),
  ].join("\n");
}

export function formatUnknownArgError(
  arg: string,
  metadata: PackageMetadata = packageMetadata,
): string {
  return [`Unknown argument: ${arg}`, "", formatUsageText(metadata)].join("\n");
}

export function formatInvalidArgsError(
  args: string[],
  metadata: PackageMetadata = packageMetadata,
): string {
  return [
    `Invalid arguments: ${args.join(" ")}`,
    "",
    formatUsageText(metadata),
  ].join("\n");
}

export function parseCliArgs(argv: string[]): CliParseResult {
  if (argv.length === 0) {
    return {
      kind: "start-server",
    };
  }

  const [arg, maybeProjectRoot] = argv;
  if (arg === "--health") {
    if (argv.length === 1) {
      return {
        kind: "run-health-check",
      };
    }

    if (argv.length !== 2) {
      return {
        kind: "error",
        reason: "invalid-arguments",
        args: argv,
      };
    }

    if (
      maybeProjectRoot === "--help" ||
      maybeProjectRoot === "-h" ||
      maybeProjectRoot === "--version" ||
      maybeProjectRoot === "-v"
    ) {
      return {
        kind: "error",
        reason: "invalid-arguments",
        args: argv,
      };
    }

    if (!path.isAbsolute(maybeProjectRoot ?? "")) {
      return {
        kind: "error",
        reason: "invalid-health-path",
        args: argv,
      };
    }

    return {
      kind: "run-health-check",
      projectRoot: maybeProjectRoot,
    };
  }

  if (argv.length !== 1) {
    return {
      kind: "error",
      reason: "invalid-arguments",
      args: argv,
    };
  }

  if (arg === "--help" || arg === "-h") {
    return {
      kind: "print-help",
    };
  }

  if (arg === "--version" || arg === "-v") {
    return {
      kind: "print-version",
    };
  }

  return {
    kind: "error",
    reason: "unknown-argument",
    args: argv,
  };
}

function writeLine(stream: WritableLike, text: string): void {
  stream.write(`${text}\n`);
}

export async function runCli(
  argv: string[],
  dependencies: RunCliDependencies,
): Promise<number> {
  const metadata = dependencies.metadata ?? packageMetadata;
  const parsed = parseCliArgs(argv);

  switch (parsed.kind) {
    case "start-server":
      await dependencies.startServer();
      return 0;
    case "run-health-check":
      return dependencies.runHealthCheck({
        projectRoot: parsed.projectRoot,
      });
    case "print-help":
      writeLine(dependencies.stdout, formatHelpText(metadata));
      return 0;
    case "print-version":
      writeLine(dependencies.stdout, metadata.version);
      return 0;
    case "error":
      writeLine(
        dependencies.stderr,
        parsed.reason === "unknown-argument"
          ? formatUnknownArgError(parsed.args[0] ?? "", metadata)
          : parsed.reason === "invalid-health-path"
            ? formatInvalidHealthPathError(parsed.args[1] ?? "", metadata)
            : formatInvalidArgsError(parsed.args, metadata),
      );
      return 1;
  }
}
